/**
 * Error handling utility functions for client-side error handling
 */

import toast from 'react-hot-toast';

/**
 * Standard error types for consistent error handling
 */
export const ErrorTypes = {
  AUTHENTICATION: 'authentication',
  VALIDATION: 'validation',
  SERVER: 'server',
  NETWORK: 'network',
  PERMISSION: 'permission',
  NOT_FOUND: 'not_found',
  UNKNOWN: 'unknown'
};

/**
 * Handles API errors consistently across the application
 * @param {Error} error - The error object from API call
 * @param {Function} callback - Optional callback function to execute after error handling
 */
export const handleApiError = (error, callback) => {
  let errorType = ErrorTypes.UNKNOWN;
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  // Determine error type and message based on the error response
  if (error.response) {
    // Server responded with an error status
    const status = error.response.status;
    const data = error.response.data;
    
    switch (status) {
      case 400:
        errorType = ErrorTypes.VALIDATION;
        errorMessage = data.message || 'Invalid request. Please check your input.';
        break;
      case 401:
        errorType = ErrorTypes.AUTHENTICATION;
        errorMessage = 'Invalid Credentials. Please log in again.';
        break;
      case 403:
        errorType = ErrorTypes.PERMISSION;
        errorMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        errorType = ErrorTypes.NOT_FOUND;
        errorMessage = data.message || 'The requested resource was not found.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = ErrorTypes.SERVER;
        errorMessage = 'Server error. Please try again later.';
        break;
      default:
        errorType = ErrorTypes.UNKNOWN;
        errorMessage = data.message || 'An error occurred. Please try again.';
    }
  } else if (error.request) {
    // Request was made but no response received
    errorType = ErrorTypes.NETWORK;
    errorMessage = 'Network error. Please check your connection and try again.';
  }
  
  // Display error toast
  toast.error(errorMessage);
  
  // Log error for debugging
  console.error(`[${errorType.toUpperCase()}]`, error);
  
  // Execute callback if provided
  if (callback && typeof callback === 'function') {
    callback(errorType, errorMessage);
  }
  
  return { type: errorType, message: errorMessage };
};

/**
 * Handles form validation errors
 * @param {Object} errors - The errors object from react-hook-form
 * @returns {Object} - Object with first error message and field
 */
export const handleFormErrors = (errors) => {
  if (!errors || Object.keys(errors).length === 0) {
    return { message: null, field: null };
  }
  
  // Get the first error
  const firstErrorField = Object.keys(errors)[0];
  const firstError = errors[firstErrorField];
  
  // Display error toast
  if (firstError.message) {
    toast.error(firstError.message);
  }
  
  return {
    message: firstError.message,
    field: firstErrorField
  };
};

/**
 * Creates a user-friendly error message from an error object
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // If it's an axios error with a response
  if (error.response && error.response.data) {
    const data = error.response.data;
    return data.message || 'An error occurred with your request';
  }
  
  // If it's a network error
  if (error.message && error.message.includes('Network Error')) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  // If it has a message property
  if (error.message) {
    return error.message;
  }
  
  // If it's a string
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected error occurred';
};

/**
 * Handles authentication-specific errors
 * @param {Error} error - The error object
 * @param {Function} logoutFn - Function to call for logging out the user
 * @returns {Object} - Error details
 */
export const handleAuthError = (error, logoutFn) => {
  const errorDetails = handleApiError(error);
  
  // If it's an authentication error, log the user out
  if (errorDetails.type === ErrorTypes.AUTHENTICATION && logoutFn) {
    // Show a message before logging out
    toast.error('Your session has expired. Please log in again.');
    
    // Log the user out
    setTimeout(() => {
      logoutFn();
    }, 2000);
  }
  
  return errorDetails;
};